import { useCallback, useEffect, useRef, useState } from "react";
import { getVoiceAnswers } from "../api/rounds";
import type { VoiceAnswerStatus } from "../types";

const POLL_INTERVAL_MS = 2000;

export function useVoiceAnswerPolling(roundId: string): {
	statuses: Map<string, VoiceAnswerStatus>;
	refresh: () => void;
} {
	const [statuses, setStatuses] = useState<Map<string, VoiceAnswerStatus>>(
		new Map(),
	);
	const [pollTrigger, setPollTrigger] = useState(0);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const activeRef = useRef(true);

	const refresh = useCallback(() => {
		setPollTrigger((n) => n + 1);
	}, []);

	useEffect(() => {
		activeRef.current = true;
		void pollTrigger;

		async function poll() {
			if (!activeRef.current) return;
			try {
				const data = await getVoiceAnswers(roundId);
				if (!activeRef.current) return;
				const map = new Map(data.map((s) => [s.question_id, s]));
				setStatuses(map);

				const hasPending = data.some(
					(s) => s.status === "pending" || s.status === "transcribing",
				);
				if (hasPending) {
					timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				}
			} catch {
				// retry on error
				if (activeRef.current) {
					timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
				}
			}
		}

		poll();

		return () => {
			activeRef.current = false;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, [roundId, pollTrigger]);

	return { statuses, refresh };
}
